/**
 * This function compares two dates to determine if one date is equal to or better than the other
 * A date is considered better if it has more information than the other date
 *
 * @param {string} betterFactDate - The date to compare
 * @param {string} comparingFactDate - The date to compare against
 * @returns {object} An object with the following properties:
 * - isEqualOrBetter: A boolean indicating if the better date is equal to or has more information than the comparing date
 * - isReliable: A boolean indicating if the comparison is reliable, (i.e. both dates are formal dates)
 *
 * **Note:** Date comparison is considered unreliable if at least one of the dates is not a formal date because it is difficult and sometimes impossible to compare dates that are not in a formal format due to the many formats and languages in which dates can be written.
 */
export function isEqualOrBetterDate({ comparingDate, betterDate }) {
  // Early return if either date is undefined or empty
  if (!comparingDate || (!comparingDate.formal && !comparingDate.text)) return createDateComparisonResult(true, true)
  if (!betterDate || (!betterDate.formal && !betterDate.text)) return createDateComparisonResult(false, true)

  const comparingDateFormatted = formatDate(comparingDate)
  const betterDateFormatted = formatDate(betterDate)

  // If dates are identical, return early
  if (
    (comparingDateFormatted?.formal && comparingDateFormatted?.formal === betterDateFormatted?.formal) ||
    (comparingDateFormatted?.text && comparingDateFormatted?.text === betterDateFormatted?.text)
  ) {
    return createDateComparisonResult(true, true)
  }

  // Compare formal dates
  if (comparingDateFormatted.formal && betterDateFormatted.formal) {
    return compareFormalDates(comparingDateFormatted.formal, betterDateFormatted.formal)
  }

  // Compare informal dates
  return compareInformalDates(comparingDateFormatted, betterDateFormatted)
}

/**
 * This function checks if a date string is a formal date
 * A formal date is defined as a string that follows the formatting rules found here: https://github.com/FamilySearch/gedcomx/blob/master/specifications/date-format-specification.md
 *
 * @param {string} dateString
 * @returns {boolean} True if the date is a formal date
 */
export function isDateFormal(dateString) {
  return dateString && !!dateString.match(/^(A|R[0-9]*\/)?\/?[+-]\d{4}/)
}

/**
 * This function checks if a date string is a simple date
 * A simple date is defined as a string that follows the formatting rules found here: https://github.com/FamilySearch/gedcomx/blob/master/specifications/date-format-specification.md
 *
 * @param {string} dateString
 * @returns {boolean} True if the date is a simple date
 */
export function isDateSimple(dateString) {
  return dateString && !!dateString.match(/^[+-]\d{4}(-\d{2})?(-\d{2})?$/)
}

/**
 * This function compares two dates to determine if they are equal or if one date has more information than the other
 * This function is used when at least one of the dates is not a formal date
 * @param {string} comparingDate - The date to compare
 * @param {string} betterDate - The date to compare against
 * @returns {boolean} True if the comparing date is equal to or has less information than the better date
 */
export function isEqualOrBetterUnreliableDates({ comparingDate, betterDate }) {
  const comparingDateObj = constructComparibleDate(comparingDate)
  const betterDateObj = constructComparibleDate(betterDate)

  if (!comparingDateObj || !betterDateObj) return false

  // It is impossible for an approximate or range date to be compared to a non-formal date
  // If both dates are formal (thus reliable), this function should not be called
  if (comparingDateObj.isApproximate || comparingDateObj.startDate || comparingDateObj.endDate) return false
  if (betterDateObj.isApproximate || betterDateObj.startDate || betterDateObj.endDate) return false

  const bothDatesAreEqualOrBetterDateHasMoreInfo =
    (comparingDateObj.date.year === betterDateObj.date.year ||
      (!comparingDateObj.date.year && betterDateObj.date.year)) &&
    (comparingDateObj.date.month === betterDateObj.date.month ||
      (!comparingDateObj.date.month && betterDateObj.date.month)) &&
    (comparingDateObj.date.day === betterDateObj.date.day || (!comparingDateObj.date.day && betterDateObj.date.day))

  return bothDatesAreEqualOrBetterDateHasMoreInfo
}

/**
 * This function constructs a date object that can be compared to other date objects
 *
 * If the date string is a formal date, it will be parsed into an object with the following properties:
 * - isApproximate: a boolean indicating if the date is approximate
 * - date: an object with year, month, and day properties (if the date is NOT a range)
 * - startDate: an object with year, month, and day properties (if the date is a range)
 * - endDate: an object with year, month, and day properties (if the date is a range)
 *
 * If the date string is not a formal date, it will be parsed into an object with a single date property
 * - date: an object with year, month, and day properties
 *
 * If the date string is not a valid date, the function returns null
 * @param {string} dateString
 */
export function constructComparibleDate(dateString) {
  const parsedFormalDate = parseFormalDate(dateString)
  if (parsedFormalDate) return parsedFormalDate

  const dateObj = createDateObj(dateString)
  if (!dateObj || Number.isNaN(dateObj.getTime())) return null

  return { date: { year: dateObj.getFullYear(), month: dateObj.getMonth() + 1, day: dateObj.getDate() } }
}

/**
 * This function parses a formal date string into an object with the following properties:
 * - isApproximate: a boolean indicating if the date is approximate
 * - date: an object with year, month, and day properties (if the date is NOT a range)
 * - startDate: an object with year, month, and day properties (if the date is a range)
 * - endDate: an object with year, month, and day properties (if the date is a range)
 *
 * A formal date is defined as a string that follows the formatting rules found here: https://github.com/FamilySearch/gedcomx/blob/master/specifications/date-format-specification.md
 *
 * If the formal date string is not a valid date, the function returns null
 *
 * @param {string} dateString - A formal date string
 * @returns {object} An object with isApproximate, date, startDate, and endDate properties
 */
export function parseFormalDate(dateString) {
  if (!dateString || typeof dateString !== 'string' || !isDateFormal(dateString)) return null

  let date = dateString
  let isApproximate = false

  // We don't need to parse recurring dates or durations for now.
  if (isRecurringDate(date) || isDuration(date)) return null

  if (isApproximateDate(date)) {
    isApproximate = true
    date = date.slice(1)
  }

  if (isRangeDate(date)) {
    let [startDate, endDate] = date.split('/')

    if (startDate && isSimpleDate(startDate)) startDate = parseSimpleDate(startDate)

    if (endDate && isSimpleDate(endDate)) endDate = parseSimpleDate(endDate)
    else if (endDate && isDuration(endDate)) endDate = null // if End date is a duration, we don't use it for comparison for now

    return { isApproximate, startDate, endDate }
  }

  if (isSimpleDate(date)) return { isApproximate, date: parseSimpleDate(date) }

  // If we can't parse the date, return null
  return null

  function isApproximateDate(d) {
    return d && d[0] === 'A'
  }
  function isRecurringDate(d) {
    return d && d[0] === 'R'
  }
  function isDuration(d) {
    return d && d[0] === 'P'
  }
  function isRangeDate(d) {
    return d && d.includes('/')
  }
  function isSimpleDate(d) {
    return d && ['+', '-'].includes(d[0])
  }
}

/**
 * Parses a simple date string in the form of "±YYYY-MM-DD..." into an object with year, month, and day properties
 *
 * **Note**: This function does not parse time of day or time zone information
 *
 * @param {string} dateString - A simple date string in the form of "±YYYY-MM-DD..."
 * @returns {object} An object with year, month, and day properties
 */
export function parseSimpleDate(dateString) {
  if (!dateString || typeof dateString !== 'string' || !isDateSimple(dateString)) return null

  const isYearNegative = dateString[0] === '-'
  const dateWithoutQualifier = dateString.slice(1)

  const [year, month, day] = dateWithoutQualifier.split('-').map((part) => parseInt(part, 10))
  return { year: isYearNegative ? -year : year, month, day }
}

/**
 * Creates a new date object from a date
 *
 * This function replaces all non alpha-numeric characters with a space and then trims off any spaces before feeding the date into the `new Date()` constructor
 * This is to support languages that use characters, such as Japanese and Chinese, that are not supported by the date constructor
 *
 * This does not support middle eastern languages or other languages that the Date constructor is not able to translate.
 * This is a temporary solution until we can find a better way to compare dates
 *
 * @param {string} dateString - A date string
 * @returns {Date} A new date object
 */
export function createDateObj(dateString) {
  return new Date(dateString?.replace(/[^a-zA-Z0-9]/g, ' ').trim())
}

/**
 * This function removes time stamps from a formal date string
 *
 * @param {string} dateString The date string to remove time stamps from
 * @returns {string} The date string with time stamps removed
 */
export function removeTimeStamps(dateString) {
  return dateString
    .split('/')
    .map((date) => date.replace(/T.*/, ''))
    .join('/')
}

/** PRIVATE HELPER FUNCTIONS */

function formatDate(date) {
  const isFormal = isDateFormal(date?.formal)
  return { formal: isFormal ? removeTimeStamps(date?.formal) : undefined, text: date?.text?.toLowerCase() }
}

function createDateComparisonResult(isEqualOrBetter, isReliable) {
  return { isEqualOrBetter, isReliable }
}

function compareFormalDates(comparingDateFormatted, betterDateFormatted) {
  const comparingDateParts = comparingDateFormatted?.split('/') || []
  const betterDateParts = betterDateFormatted?.split('/') || []

  const isDateEqualOrBetter =
    comparingDateParts.length === betterDateParts.length &&
    betterDateParts.every(
      (betterPart, i) => betterPart === comparingDateParts[i] || betterPart?.startsWith(comparingDateParts[i])
    )

  return createDateComparisonResult(isDateEqualOrBetter, true)
}

function compareInformalDates(comparingDate, betterDate) {
  const comparingDateVal = comparingDate?.formal || comparingDate?.text
  const betterDateVal = betterDate?.formal || betterDate?.text

  const isDateEqualOrBetter = isEqualOrBetterUnreliableDates({
    comparingDate: comparingDateVal,
    betterDate: betterDateVal,
  })

  return createDateComparisonResult(isDateEqualOrBetter, false)
}
